import { Chip } from "@mui/material";
import {
  Cancel,
  Check,
  CancelScheduleSend,
  BrowserUpdated,
  CoPresent,
  QueryBuilder,
  ReceiptLong,
  PriceCheck,
  AssignmentTurnedIn,
  Close,
  Reply,
  Visibility,
} from "@mui/icons-material";

import type { ReactElement } from "react";
import type { TFunction } from "i18next";
import type { TranslationKey } from "@shared/i18n/translationKey";

// Config presentacional de los chips de factura. Cada mapa asocia un valor de
// dominio con su meta de presentación ({ icon?, labelKey, color? }); los render
// helpers arman el Chip. Reemplaza cinco switch que repetían el mismo markup.

interface ChipMeta {
  icon?: ReactElement;
  labelKey: TranslationKey;
  color?: string;
}

interface TypeMeta extends Omit<ChipMeta, "labelKey"> {
  // "Digital" no tiene `labelKey`: usa el valor crudo como label (`useRawValue`), ver `types()`.
  labelKey?: TranslationKey;
  useRawValue?: boolean;
}

const colorSx = (color?: string) => (color ? { backgroundColor: color, color: "#fff" } : undefined);

// Subtipo de factura (con ícono). El default cubre "Horas" y cualquier otro valor.
const SUBTYPE_META: Record<string, ChipMeta> = {
  Administrativa: { icon: <ReceiptLong style={{ color: "white" }} />, labelKey: "administrative", color: "#347ed6" },
};
const SUBTYPE_DEFAULT: ChipMeta = { icon: <QueryBuilder style={{ color: "white" }} />, labelKey: "hours", color: "#2c387e" };

const subType = (value: string, t: TFunction): ReactElement => {
  const meta = SUBTYPE_META[value] ?? SUBTYPE_DEFAULT;
  return <Chip icon={meta.icon} label={t(meta.labelKey)} sx={colorSx(meta.color)} />;
};

// Tipo de factura. "Digital" usa el valor crudo como label (comportamiento original).
const TYPE_META: Record<string, TypeMeta> = {
  Personal: { icon: <CoPresent />, labelKey: "staff", color: "#7EB207" },
  Digital: { icon: <BrowserUpdated style={{ color: "white" }} />, useRawValue: true, color: "#0B9BC5" },
};

const types = (value: string, t: TFunction): ReactElement | undefined => {
  const meta = TYPE_META[value];
  if (!meta) return undefined;
  return (
    <Chip icon={meta.icon} label={meta.useRawValue ? value : t(meta.labelKey as TranslationKey)} sx={colorSx(meta.color)} />
  );
};

// Estado de factura del CRM (en español, con ícono). Los estados sin color usan
// el Chip por defecto de MUI.
const STATUS_ICON_META: Record<string, ChipMeta> = {
  "Pendiente de revisión": { icon: <Visibility />, labelKey: "pendingReview" },
  Reenviada: { icon: <Reply />, labelKey: "forward" },
  Rechazada: { icon: <CancelScheduleSend sx={{ color: "#fff" }} />, labelKey: "rejected", color: "#d47510" },
  Aprobada: { icon: <Check sx={{ color: "#fff" }} />, labelKey: "accepted", color: "#05a724" },
  Cancelada: { icon: <Cancel style={{ color: "white" }} />, labelKey: "cancelled", color: "#d42d10" },
  Pagada: { icon: <PriceCheck sx={{ color: "#fff" }} />, labelKey: "paid", color: "#33cb4c" },
  "Pago en proceso": { icon: <AssignmentTurnedIn sx={{ color: "#fff" }} />, labelKey: "posted", color: "#05a724" },
};

const status = (value: string, t: TFunction): ReactElement | undefined => {
  const meta = STATUS_ICON_META[value];
  if (!meta) return undefined;
  return <Chip icon={meta.icon} label={t(meta.labelKey)} sx={colorSx(meta.color)} />;
};

// Indicador booleano "SI/NO" (pagada / consolidada comparten el mismo chip).
const yesNoChip = (value: boolean): ReactElement =>
  value ? (
    <Chip icon={<Check />} label="SI" sx={{ backgroundColor: "#7EB207", color: "#fff" }} />
  ) : (
    <Chip icon={<Close sx={{ color: "#fff" }} />} label="NO" sx={{ backgroundColor: "#ffba2e", color: "#fff" }} />
  );

const paid = (value: boolean): ReactElement => yesNoChip(value);
const consolidated = (value: boolean): ReactElement => yesNoChip(value);

// Estados del documento (enum estable en inglés del backend), sin ícono.
const DOC_STATUS_META: Record<string, ChipMeta> = {
  CREATED: { labelKey: "created", color: "#2962ff" },
  SENT: { labelKey: "sent", color: "#4bd86d" },
  REJECTED: { labelKey: "rejected", color: "#d47510" },
  CANCELLED: { labelKey: "cancelled", color: "#d42d10" },
  REPLACED: { labelKey: "replaced", color: "#f9a825" },
  PAID: { labelKey: "paid", color: "#9c27b0" },
  POSTED: { labelKey: "posted", color: "#0d47a1" },
  ACCEPTED: { labelKey: "accepted", color: "#4caf50" },
};

// Estados en español que la grilla de facturas también puede mostrar (sin ícono).
const STATUS_ES_META: Record<string, ChipMeta> = {
  "Pendiente de revisión": { labelKey: "pendingReview" },
  Pagada: { labelKey: "paid", color: "#9c27b0" },
  Rechazada: { labelKey: "rejected", color: "#d47510" },
  Aprobada: { labelKey: "accepted", color: "#05a724" },
  Cancelada: { labelKey: "cancelled", color: "#d42d10" },
  Reenviada: { labelKey: "forward", color: "#05a724" },
  "Pago en proceso": { labelKey: "posted", color: "#0d47a1" },
};

const FOR_INVOICE_META: Record<string, ChipMeta> = { ...DOC_STATUS_META, ...STATUS_ES_META };

const colorChip = (meta: ChipMeta, t: TFunction): ReactElement => <Chip label={t(meta.labelKey)} sx={colorSx(meta.color)} />;

const statusPreInvoice = (value: string, t: TFunction): ReactElement | undefined => {
  const meta = DOC_STATUS_META[value];
  return meta ? colorChip(meta, t) : undefined;
};

const statusForInvoice = (value: string, t: TFunction): ReactElement | undefined => {
  const meta = FOR_INVOICE_META[value];
  return meta ? colorChip(meta, t) : undefined;
};

export { subType, types, status, paid, consolidated, statusPreInvoice, statusForInvoice };
